import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ExternalLink } from "lucide-react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { BRANDS } from "@/lib/brands";
import { Reveal } from "@/components/site/Reveal";
import useSEO from "@/lib/useSEO";

const EASE = [0.22, 1, 0.36, 1];

export default function BrandDetail() {
  const { slug } = useParams();
  const brand = BRANDS.find((b) => b.slug === slug);
  useSEO(
    brand
      ? {
          title: `${brand.name} — ${brand.tagline} | RSA Overseas`,
          description: brand.heroLine || brand.short,
          path: `/brands/${brand.slug}`,
          image: brand.image,
        }
      : { title: "Brand not found — RSA Overseas", path: "/brands" },
  );
  if (!brand) return <Navigate to="/brands" replace />;

  const idx = BRANDS.findIndex((b) => b.slug === slug);
  const next = BRANDS[(idx + 1) % BRANDS.length];

  return (
    <div
      data-testid={`brand-detail-${brand.slug}`}
      className="bg-parchment text-aubergine"
    >
      <BrandHero brand={brand} />
      <Manifesto brand={brand} />
      <Pillars brand={brand} />
      {brand.diet && <DietBreakdown brand={brand} />}
      <ProcessSection brand={brand} />
      <ProductsAndVoice brand={brand} />
      <NextBrand next={next} />
    </div>
  );
}

/* ————————————————— HERO ————————————————— */
function BrandHero({ brand }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.1]);

  return (
    <section
      ref={ref}
      className="relative bg-aubergine text-parchment overflow-hidden"
    >
      <div className="relative h-[85vh] min-h-[620px] max-h-[880px] w-full overflow-hidden">
        <motion.img
          src={brand.image}
          alt={brand.name}
          className="absolute inset-0 h-[112%] w-full object-cover"
          style={{ y, scale }}
          draggable={false}
        />
        <div
          aria-hidden
          className="absolute inset-0 bg-gradient-to-t from-aubergine via-aubergine/50 to-aubergine/20"
        />
        <div
          aria-hidden
          className="absolute inset-0 bg-gradient-to-r from-aubergine/85 via-aubergine/10 to-transparent"
        />
        <div aria-hidden className="absolute inset-0 grain-overlay opacity-70" />

        <div className="absolute inset-0 flex flex-col">
          <div className="mx-auto w-full max-w-[1440px] px-6 md:px-12 lg:px-16 pt-14 md:pt-20">
            <Link
              to="/brands"
              data-testid="brand-back-link"
              className="inline-flex items-center gap-2 text-parchment/75 hover:text-magenta transition-colors text-[11px] tracking-[0.28em] uppercase font-semibold"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              All Brands
            </Link>
          </div>

          <div className="mt-auto mx-auto w-full max-w-[1440px] px-6 md:px-12 lg:px-16 pb-16 md:pb-24">
            <div className="grid lg:grid-cols-12 gap-x-14 gap-y-10 items-end">
              <div className="lg:col-span-8">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.9, delay: 0.1, ease: EASE }}
                  className="text-[10px] uppercase tracking-[0.32em] font-semibold text-magenta flex items-center gap-4"
                >
                  <span className="h-px w-8 bg-magenta" />
                  {brand.species}
                </motion.div>
                <motion.h1
                  data-testid="brand-detail-heading"
                  initial={{ opacity: 0, y: 40, filter: "blur(6px)" }}
                  animate={{ opacity: 1, y: 0, filter: "blur(0)" }}
                  transition={{ duration: 1.1, delay: 0.2, ease: EASE }}
                  className="font-display font-light mt-6 text-7xl md:text-8xl lg:text-[144px] xl:text-[176px] tracking-[-0.05em] leading-[0.86] text-parchment"
                >
                  {brand.name}
                </motion.h1>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.9, delay: 0.5, ease: EASE }}
                  className="mt-6 font-display italic font-light text-2xl md:text-3xl lg:text-4xl text-magenta max-w-3xl leading-tight"
                >
                  {brand.tagline}
                </motion.div>
              </div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.7, ease: EASE }}
                className="lg:col-span-4 lg:pl-6"
              >
                <p className="text-parchment/80 leading-relaxed max-w-md">
                  {brand.heroLine}
                </p>
                {brand.website && (
                  <a
                    href={brand.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-6 inline-flex items-center gap-2 text-parchment hover:text-magenta transition-colors text-[11px] tracking-[0.28em] uppercase font-semibold border-b border-parchment/40 hover:border-magenta pb-1.5"
                  >
                    Visit{" "}
                    {brand.website
                      .replace(/^https?:\/\//, "")
                      .replace(/\/$/, "")}
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                )}
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ————————————————— MANIFESTO ————————————————— */
function Manifesto({ brand }) {
  return (
    <section className="bg-parchment">
      <div className="mx-auto max-w-[1200px] px-6 md:px-12 lg:px-16 py-24 md:py-36 text-center">
        <Reveal>
          <div className="text-[10px] uppercase tracking-[0.32em] font-semibold text-magenta">
            The Idea
          </div>
          <h2 className="font-display font-light text-4xl md:text-6xl lg:text-7xl mt-10 tracking-[-0.035em] leading-[1] text-aubergine text-balance">
            <span className="italic">&ldquo;{brand.manifesto}&rdquo;</span>
          </h2>
          <div className="mt-10 inline-flex items-center gap-4 text-[10px] uppercase tracking-[0.32em] font-semibold text-magenta">
            <span className="h-px w-8 bg-magenta" />
            {brand.name}
            <span className="h-px w-8 bg-magenta" />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ————————————————— PILLARS ————————————————— */
function Pillars({ brand }) {
  return (
    <section className="bg-aubergine text-parchment relative overflow-hidden grain-overlay">
      <div className="relative mx-auto max-w-[1440px] px-6 md:px-12 lg:px-16 py-24 md:py-32">
        <Reveal>
          <div className="grid md:grid-cols-12 gap-y-10 items-end mb-16">
            <div className="md:col-span-8">
              <div className="text-[10px] uppercase tracking-[0.32em] font-semibold text-magenta/90 flex items-center gap-4">
                <span className="h-px w-8 bg-magenta/60" />
                The Collection
              </div>
              <h2 className="font-display font-light text-5xl md:text-6xl lg:text-7xl mt-6 tracking-[-0.035em] leading-[0.94] text-parchment text-balance">
                Four pillars of{" "}
                <span className="italic text-magenta">complete care.</span>
              </h2>
            </div>
          </div>
        </Reveal>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-parchment/10">
          {brand.pillars.map((p, i) => (
            <Reveal key={p.label} delay={(i % 4) * 0.08}>
              <motion.article
                whileHover={{ y: -4 }}
                transition={{ duration: 0.4, ease: EASE }}
                className="bg-aubergine p-8 md:p-10 h-full"
              >
                <div className="text-[10px] uppercase tracking-[0.32em] font-semibold text-magenta">
                  0{i + 1}
                </div>
                <h3 className="font-display font-light text-4xl md:text-5xl mt-6 tracking-[-0.02em] text-parchment leading-tight">
                  {p.label}
                </h3>
                <div className="mt-4 font-display italic text-lg text-magenta/90">
                  {p.items}
                </div>
                <p className="mt-6 text-parchment/60 leading-relaxed text-sm">
                  {p.note}
                </p>
              </motion.article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ————————————————— DIET (Hopsy only) ————————————————— */
function DietBreakdown({ brand }) {
  return (
    <section className="bg-parchment">
      <div className="mx-auto max-w-[1440px] px-6 md:px-12 lg:px-16 py-24 md:py-32">
        <div className="grid lg:grid-cols-12 gap-x-16 gap-y-14 items-center">
          <div className="lg:col-span-5">
            <Reveal>
              <div className="text-[10px] uppercase tracking-[0.32em] font-semibold text-magenta">
                The Perfect Diet
              </div>
              <h2 className="font-display font-light text-5xl md:text-6xl lg:text-7xl mt-8 tracking-[-0.035em] leading-[0.94] text-aubergine text-balance">
                Small animal,{" "}
                <span className="italic text-magenta">big nutrition.</span>
              </h2>
              <p className="mt-8 text-lg text-ink/70 leading-relaxed max-w-md">
                A rabbit or guinea pig fed on hay first is a rabbit or guinea
                pig that lives long and stays quietly happy. Here is the daily
                ratio we live by.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <div className="grid sm:grid-cols-2 gap-px bg-aubergine/15 border border-aubergine/15">
              {brand.diet.map((d, i) => (
                <Reveal key={d.label} delay={(i % 2) * 0.1}>
                  <motion.div
                    whileHover={{ backgroundColor: "#F5EDDF" }}
                    transition={{ duration: 0.3 }}
                    className="bg-parchment p-8 md:p-10"
                  >
                    <div className="font-display font-light text-6xl md:text-7xl tracking-[-0.04em] text-magenta leading-none">
                      {d.pct}
                    </div>
                    <div className="mt-4 font-display text-2xl text-aubergine">
                      {d.label}
                    </div>
                    <div className="mt-2 text-sm text-ink/60 italic">
                      {d.note}
                    </div>
                  </motion.div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ————————————————— PROCESS ————————————————— */
function ProcessSection({ brand }) {
  return (
    <section className="bg-parchment border-t border-aubergine/15">
      <div className="mx-auto max-w-[1440px] px-6 md:px-12 lg:px-16 py-24 md:py-32">
        <Reveal>
          <div className="grid md:grid-cols-12 gap-y-10 items-end mb-16 md:mb-20">
            <div className="md:col-span-8">
              <div className="text-[10px] uppercase tracking-[0.32em] font-semibold text-magenta flex items-center gap-4">
                <span className="h-px w-8 bg-magenta" />
                From Field to Feed
              </div>
              <h2 className="font-display font-light text-5xl md:text-6xl lg:text-7xl mt-8 tracking-[-0.035em] leading-[0.94] text-aubergine text-balance">
                {brand.process.length} patient steps —{" "}
                <span className="italic text-magenta">each done by hand.</span>
              </h2>
            </div>
            <div className="md:col-span-4 md:pl-6">
              <p className="text-ink/70 leading-relaxed max-w-sm">
                Nothing here is automated for the sake of speed. Each step
                exists because the alternative shows up in the animal or the
                pantry.
              </p>
            </div>
          </div>
        </Reveal>

        <div className="relative">
          <div
            aria-hidden
            className="absolute left-0 right-0 top-8 md:top-10 h-px bg-aubergine/15 hidden md:block"
          />
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-y-14 gap-x-6 relative">
            {brand.process.map((p, i) => (
              <Reveal
                key={p.t}
                delay={(i % 4) * 0.08}
                className="relative"
              >
                <div className="relative flex items-center gap-3 mb-6">
                  <div className="h-4 w-4 md:h-5 md:w-5 rounded-full bg-magenta ring-4 ring-parchment shrink-0" />
                  <div className="text-[10px] uppercase tracking-[0.32em] font-semibold text-magenta">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                </div>
                <h3 className="font-display font-light text-2xl md:text-3xl text-aubergine tracking-[-0.02em]">
                  {p.t}
                </h3>
                <p className="mt-3 text-sm text-ink/65 leading-relaxed">
                  {p.d}
                </p>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ————————————————— PRODUCTS + VOICE ————————————————— */
function ProductsAndVoice({ brand }) {
  return (
    <section className="bg-aubergine text-parchment relative overflow-hidden grain-overlay">
      <div
        aria-hidden
        className="absolute -top-32 -right-32 h-96 w-96 rounded-full bg-magenta/25 blur-3xl"
      />
      <div className="relative mx-auto max-w-[1440px] px-6 md:px-12 lg:px-16 py-24 md:py-32">
        <div className="grid lg:grid-cols-12 gap-x-16 gap-y-16">
          <Reveal className="lg:col-span-7">
            <div className="text-[10px] uppercase tracking-[0.32em] font-semibold text-magenta/90 flex items-center gap-4">
              <span className="h-px w-8 bg-magenta/60" />
              Kind Words
            </div>
            <figure className="mt-10 relative">
              <span
                aria-hidden
                className="absolute -top-10 -left-2 font-display italic text-8xl text-magenta/30 leading-none pointer-events-none select-none"
              >
                &ldquo;
              </span>
              <blockquote className="relative font-display italic font-light text-3xl md:text-4xl lg:text-5xl tracking-[-0.02em] leading-[1.15] text-parchment text-balance">
                {brand.voice.quote}
              </blockquote>
              <figcaption className="mt-8 flex items-center gap-4">
                <span className="h-px w-10 bg-magenta" />
                <span className="font-display text-xl text-parchment">
                  {brand.voice.by}
                </span>
                <span className="text-[10px] uppercase tracking-[0.28em] text-parchment/60 font-semibold">
                  {brand.voice.role}
                </span>
              </figcaption>
            </figure>
          </Reveal>

          <Reveal className="lg:col-span-5" delay={0.15}>
            <div className="border border-parchment/20 p-10 md:p-12 h-full">
              <div className="text-[10px] uppercase tracking-[0.32em] font-semibold text-magenta">
                Featured products
              </div>
              <ul className="mt-8 divide-y divide-parchment/15">
                {brand.products.map((p, i) => (
                  <motion.li
                    key={p}
                    whileHover={{ x: 6 }}
                    transition={{ duration: 0.3, ease: EASE }}
                    className="flex items-baseline gap-6 py-5 text-parchment"
                  >
                    <span className="font-display italic font-light text-3xl text-magenta w-12">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="font-display text-xl tracking-[-0.01em]">
                      {p}
                    </span>
                  </motion.li>
                ))}
              </ul>
              <Link
                to="/contact"
                data-testid="brand-detail-cta"
                className="mt-10 w-full inline-flex items-center justify-center gap-3 bg-magenta hover:bg-parchment hover:text-aubergine text-parchment text-[11px] tracking-[0.28em] uppercase font-semibold px-6 py-5 transition-all duration-300"
              >
                Request {brand.name} Pricing
                <span className="h-px w-6 bg-current" />
              </Link>
              {brand.website && (
                <a
                  href={brand.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-4 w-full inline-flex items-center justify-center gap-2 text-parchment/85 hover:text-magenta text-[11px] tracking-[0.28em] uppercase font-semibold border border-parchment/25 hover:border-magenta py-4 transition-colors"
                >
                  Shop at{" "}
                  {brand.website
                    .replace(/^https?:\/\//, "")
                    .replace(/\/$/, "")}
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* ————————————————— NEXT BRAND ————————————————— */
function NextBrand({ next }) {
  return (
    <section className="bg-parchment border-t border-aubergine/15">
      <Link
        to={`/brands/${next.slug}`}
        data-testid="brand-next-link"
        className="block group"
      >
        <div className="mx-auto max-w-[1440px] px-6 md:px-12 lg:px-16 py-16 md:py-20 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.32em] font-semibold text-magenta">
              The next brand
            </div>
            <div className="font-display font-light mt-4 text-5xl md:text-6xl lg:text-7xl tracking-[-0.035em] text-aubergine group-hover:text-magenta transition-colors">
              {next.name}
            </div>
            <div className="mt-3 italic font-display text-ink/60">
              {next.tagline}
            </div>
          </div>
          <div className="text-[11px] tracking-[0.28em] uppercase font-semibold text-aubergine group-hover:text-magenta transition-colors flex items-center gap-3">
            Continue
            <span className="h-px w-10 bg-current transition-all group-hover:w-16" />
          </div>
        </div>
      </Link>
    </section>
  );
}
